import React from "react";
import { graphql, useFragment } from "react-relay";

import cx from "classnames";

import { MeetingContent, useShouldShowNarrowContent } from "~/meetings/MeetingContent";
import AskFellowPanel from "~/nav/panels/AskFellow";
import { Modal } from "~/ui/legacyFellowIndex";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";

import { useMeetingModalContext } from "./MeetingModalContext";

import type { MeetingContent_meetingRecording$data } from "~/__generated__/MeetingContent_meetingRecording.graphql";
import type { MeetingContent_viewer$key } from "~/__generated__/MeetingContent_viewer.graphql";
import type { MeetingModal_note$key } from "~/__generated__/MeetingModal_note.graphql";

import styles from "./MeetingModal.module.css";

const MeetingModalFragment = graphql`
	fragment MeetingModal_note on Note {
		id
		...MeetingContent_note
	}
`;

export default function MeetingModal({
	viewer,
	note: noteKey,
	recordings,
	closeModal,
	initialMeetingRecordingId,
	initialVideoTimestamp,
}: {
	viewer: MeetingContent_viewer$key;
	note: MeetingModal_note$key | null;
	recordings: readonly MeetingContent_meetingRecording$data[];
	closeModal: () => void;
	initialMeetingRecordingId?: string;
	initialVideoTimestamp?: number;
}) {
	const { isNarrow, ref: meetingCardRef } = useShouldShowNarrowContent();
	const meetingModalContext = useMeetingModalContext();
	const { isAskFellowOpen, setIsAskFellowOpen } = meetingModalContext ?? {};

	const onCloseAskFellow = React.useCallback(() => {
		setIsAskFellowOpen?.(false);
	}, [setIsAskFellowOpen]);

	const note = useFragment(MeetingModalFragment, noteKey);

	return (
		<Modal.Body noPadding className={styles.meetingModalBody} ref={meetingCardRef}>
			{isAskFellowOpen && (
				<div className={styles.askFellowPanelWrapper}>
					<AskFellowPanel contextId={note?.id} compactLandingPage={true} onClose={onCloseAskFellow} />
				</div>
			)}

			<div className={styles.meetingContentArea}>
				<MeetingContent
					viewer={viewer}
					note={note}
					recordings={recordings}
					closeModal={closeModal}
					initialMeetingRecordingId={initialMeetingRecordingId}
					initialVideoTimestamp={initialVideoTimestamp}
					isNarrow={isNarrow}
				/>
			</div>
		</Modal.Body>
	);
}

export function LoadingMeetingModal() {
	return (
		<div className={styles.meetingModalBody}>
			<div className={cx(styles.meetingContentArea, styles.isLoading)}>
				<LoadingSpinner />
			</div>
		</div>
	);
}
